import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { influencerAPI } from '../services/influencerApi';

const InfluencerAuthContext = createContext(null);

export const InfluencerAuthProvider = ({ children }) => {
  const [influencer, setInfluencer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('influencer_token');
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await influencerAPI.getMe();
      if (response.status === 'success') {
        setInfluencer(response.data);
      } else {
        // Token invalid
        influencerAPI.clearToken();
      }
    } catch (err) {
      console.error('Erro ao verificar autenticação:', err);
      influencerAPI.clearToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Request magic link
  const requestMagicLink = async (email) => {
    setError(null);
    try {
      const response = await influencerAPI.requestMagicLink(email);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Verify magic link token
  const verifyToken = async (token) => {
    setError(null);
    setLoading(true);

    try {
      const response = await influencerAPI.verifyMagicLink(token);
      
      if (response.status === 'success' && response.data?.influencer) {
        setInfluencer(response.data.influencer);
        return response.data;
      }
      
      throw new Error(response.message || 'Token inválido');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = () => {
    influencerAPI.logout();
    setInfluencer(null);
    setError(null);
  };

  // Update profile
  const updateProfile = async (data) => {
    try {
      const response = await influencerAPI.updateMe(data);
      if (response.status === 'success') {
        // Refresh influencer data
        await checkAuth();
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const value = {
    influencer,
    loading,
    error,
    isAuthenticated: !!influencer,
    requestMagicLink,
    verifyToken,
    logout,
    updateProfile,
    refreshAuth: checkAuth
  };

  return (
    <InfluencerAuthContext.Provider value={value}>
      {children}
    </InfluencerAuthContext.Provider>
  );
};

export const useInfluencerAuth = () => {
  const context = useContext(InfluencerAuthContext);
  if (!context) {
    throw new Error('useInfluencerAuth must be used within InfluencerAuthProvider');
  }
  return context;
};

export default InfluencerAuthContext;

